import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Update, GetById, GetEmployeeList, GetActiveLoansByEmployee } from "adminApi/LoanInstallmentApi/LoanInstallmentApi";
import { validateForm } from "utils/validation";
import { LoanInstallmentRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditLoanInstallmentForm from "./components/EditLoanInstallmentForm";
import fields from "../json/LoanInstallment.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import useFetchAllData from "Hooks/useFetchAllData";

const buildPayload = (formValues) => ({
    employee_id: formValues.employee_id,
    grand_loan_id: formValues.grand_loan_id,
    installment_amount: formValues.installment_amount,
    payment_amount: formValues.payment_amount,
    payment_date: formValues.payment_date,
    receiver_id: formValues.receiver_id,
    installment_no: formValues.installment_no,
    notes: formValues.notes,
});

const EditLoanInstallment = () => {
    const navigate = useNavigate();
    const { formdata } = fields;
    const { id } = useParams();
    const [loanOptions, setLoanOptions] = useState([]);
    const [loadingLoans, setLoadingLoans] = useState(false);

    // Initialize form values from JSON fields
    const initialValues = formdata.reduce(
        (acc, field) => ({ ...acc, [field.name]: "" }),
        {},
    );

    const { values, errors, handleChange, setErrors, setValues } = useFormChange(
        initialValues,
        LoanInstallmentRules,
    );

    // Fetch existing data by ID and prepopulate form
    const { loading: loadingFetch } = useFetchFormDataById(
        id,
        formdata,
        GetById,
        setValues,
    );
    const fetchTakers = React.useCallback(() => GetEmployeeList("taker", id), [id]);
    const fetchReceivers = React.useCallback(() => GetEmployeeList(), []);

    const { data: employeeOptions, loading: loadingEmployees } =
        useFetchAllData(fetchTakers);

    const { data: receiverOptions, loading: loadingReceivers } =
        useFetchAllData(fetchReceivers);

    // Fetch active loans when employee changes
    useEffect(() => {
        const fetchLoans = async () => {
            if (!values.employee_id) {
                setLoanOptions([]);
                return;
            }
            setLoadingLoans(true);
            try {
                const response = await GetActiveLoansByEmployee(values.employee_id, id);
                if (response.data.success) {
                    setLoanOptions(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching loans:", error);
            } finally {
                setLoadingLoans(false);
            }
        };

        fetchLoans();
    }, [values.employee_id]);

    // Form submit
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            return await Update({ id, data: buildPayload(formValues) });
        },
        (vals) => {
            const err = validateForm(vals, LoanInstallmentRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Loan installment updated successfully",
    );

    return (
        <>
            {(loadingSubmit || loadingFetch || loadingEmployees || loadingReceivers || loadingLoans) && (
                <Spinner isSpinner={true} />
            )}
            <EditLoanInstallmentForm
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loadingSubmit}
                employeeOptions={employeeOptions}
                receiverOptions={receiverOptions}
                loanOptions={loanOptions}
            />
        </>
    );
};

export default EditLoanInstallment;
